import React, { useState } from "react";
import "./style.scss";
import EAButton from "../shared/Buttons/EAButton";
import EATextField from "../shared/TextField/EATextField";
import EACheckbox from "../shared/Checkbox/EACheckbox";
import EAAlert from "../shared/EAAlert";
import useCardPurchaseLookup from "./useCardPurchaseLookup";
import { Ticket } from "../../models/Ticket";

const MAX_ATTEMPTS = 5;
const TIME_WINDOW = 5 * 60 * 1000; // 5 minut w ms
const COOLDOWN_MS = 5000; // 5 sekund

const HIDE_ENTRANCES_ASSORT_IDS = ["1367", "1512", "27"];

const shouldHideEntrances = (ticket: Ticket): boolean => {
  if (!ticket.assortIds) {
    return false;
  }
  const ids = String(ticket.assortIds)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  return HIDE_ENTRANCES_ASSORT_IDS.some((hiddenId) => ids.includes(hiddenId));
};

const CardPurchaseLookup: React.FC = () => {
  const [cardNumber, setCardNumber] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { getDetailsByCardID, cardDetails } = useCardPurchaseLookup();

  const [cooldown, setCooldown] = useState(false);
  const [attempts, setAttempts] = useState<number[]>([]);
  const [localError, setLocalError] = useState("");
  const [wasChecked, setWasChecked] = useState(false);

  const handleCheck = () => {
    if (cooldown) {
      return;
    }

    const now = Date.now();
    const windowStart = now - TIME_WINDOW;

    const recentAttempts = attempts.filter((t) => t >= windowStart);

    if (recentAttempts.length >= MAX_ATTEMPTS) {
      setLocalError("Zbyt wiele prób. Spróbuj ponownie za kilka minut.");
      return;
    }

    setLocalError("");
    setWasChecked(true);

    getDetailsByCardID("273", cardNumber);

    setAttempts([...recentAttempts, now]);

    setCooldown(true);
    setTimeout(() => {
      setCooldown(false);
    }, COOLDOWN_MS);
  };

  const isButtonDisabled =
    !cardNumber.trim() ||
    !isConfirmed ||
    cooldown ||
    attempts.filter((t) => t >= Date.now() - TIME_WINDOW).length >= MAX_ATTEMPTS;

  const hasPositiveBalance =
    Number(cardDetails?.captionBalanceII.replace(/<br\s*\/?>/gi, "").match(/[\d.]+$/)?.[0] || "0") > 0;

  return (
    <div className="card-purchase-lookup">
      <div className="card-purchase-lookup__title">
        <h4>Sprawdź status swojej karty</h4>
        <p>
          Wprowadź numer swojej karty <strong>(bez kropek)</strong>, aby wyświetlić przypisane do niej saldo i aktywne
          karnety.
        </p>
      </div>

      <div className="card-purchase-lookup__input">
        <div className="card-purchase-lookup__input__label">
          <EATextField
            name="cardNumber"
            label="Numer karty"
            placeholder="Wpisz numer karty (bez kropek)"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
          />

          <EACheckbox
            name="cardConfirmation"
            checked={isConfirmed}
            onChange={setIsConfirmed}
            label={<>Oświadczam, że jestem właścicielem karty/karnetu i że numer jest prawidłowy.</>}
          />

          <div className="card-purchase-lookup__button">
            <EAButton title={cooldown ? "Poczekaj..." : "Sprawdź"} onClick={handleCheck} disabled={isButtonDisabled} />
          </div>
        </div>
      </div>

      {localError && <EAAlert type="error" message={localError} />}

      {!cardDetails || !cardDetails.closeDate ? (
        <>{wasChecked && <EAAlert type="error" message="Podany kod karty jest nieprawidłowy" />}</>
      ) : (
        <div className="card-purchase-lookup__wrapper">
          <section className="ecard">
            <header className="ecard__header">
              <h2 className="ecard__title">Środki na koncie</h2>
            </header>

            {hasPositiveBalance ? (
              <div className="ecard__grid">
                <article className="ecard__tile is-positive">
                  <div className="ecard__tile-label">
                    {cardDetails?.captionBalanceII.includes("<br/>")
                      ? cardDetails?.captionBalanceII.slice(0, cardDetails?.captionBalanceII.indexOf("<br/>"))
                      : ""}
                  </div>
                  <div className="ecard__tile-amount">
                    {cardDetails?.captionBalanceII.includes("<br/>")
                      ? cardDetails?.captionBalanceII.slice(
                          cardDetails?.captionBalanceII.indexOf("<br/>") + "<br/>".length,
                        )
                      : ""}{" "}
                  </div>

                  <div className="ecard__close">
                    Koniec ważności:{" "}
                    <span className="ecard__close-date">
                      {cardDetails.closeDate ? new Date(cardDetails.closeDate).toLocaleDateString("pl-PL") : "—"}
                    </span>
                  </div>

                  <div className="ecard__warning">
                    <EAAlert type="warning" compact message="Środki po upływie terminu ważności przepadają." />
                  </div>
                </article>
              </div>
            ) : (
              <EAAlert type="warning" message="Na tej karcie nie ma środków" />
            )}
          </section>

          {cardDetails?.passes.map((item: Ticket, index: number) => (
            <div key={index}>
              <div className="ecard__belt">
                <div className="ecard__belt-name">{item.name || "—"}</div>

                <div className="ecard__belt-valid">
                  Ważność: {item.validfrom ? new Date(item.validfrom).toLocaleDateString("pl-PL") : "—"} -{" "}
                  {item.validto ? new Date(item.validto).toLocaleDateString("pl-PL") : "—"}
                </div>

                <div className="ecard__belt-counts">
                  {!shouldHideEntrances(item) && <span>Wejść: {item.entrances}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardPurchaseLookup;
